import { Component, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Bundle, ChargeItem, Invoice } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { MatDialog } from '@angular/material/dialog';
import { ChipsDirective } from '../chips.directive';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { firstValueFrom, catchError, of, Observable, map, tap, switchMap } from 'rxjs';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UtilityService } from '../shared/utility.service';
import { CustomTabsComponent, TabItem } from '../shared/custom-tabs/custom-tabs.component';
import { DisableSortParam } from '../loading.interceptor';
import { PatientFilterComponent } from '../shared/patient-filter/patient-filter.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { provideNativeDateAdapter } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { TimeService } from '../services/time.service';
export type InvoiceType =
  | 'DiagnosticReport'
  | 'ImagingStudy'
  | 'Immunization'
  | 'MedicationAdministration'
  | 'MedicationDispense'
  | 'Observation'
  | 'Procedure'
  | 'SupplyDelivery'
  | 'Laboratory'
  | 'other';

type QuerySearchCriterion = { key: string; value: string[] };

@Component({
  selector: 'app-invoice',
  standalone: true,
  imports: [
    // prefer minimal local imports; base class/template already relies on common table setup
    CommonModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    CustomTabsComponent,
    ChipsDirective,
    EmptyStateComponent,
    ReferenceDisplayDirective,
    RouterModule,
    PatientFilterComponent,
    MatMenuModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe, provideNativeDateAdapter()],

  templateUrl: './invoice.component.html',
  styleUrls: ['../medicine-requests/medicine-requests.component.scss', './invoice.component.scss']
})
export class InvoiceComponent extends AdmittedPatientsComponent {
  // private http = inject(HttpClient);
  private backendEndPoint = inject(backendEndPointToken);
  utilityService = inject(UtilityService);
  override dialog = inject(MatDialog);
  private readonly timeService = inject(TimeService);

  invoiceTypeTab: 'all' | InvoiceType = 'all';

  private chargeItemCache = new Map<string, ChargeItem>();
  effectivePatientId: string | null = null;

  // Date Period Filtering
  periodPresets = [
    { id: 'today', label: 'Today', days: 0 },
    { id: '7d', label: '7 Days', days: 7 },
    { id: '30d', label: '30 Days', days: 30 },
    { id: '90d', label: '90 Days', days: 90 },
    { id: '6m', label: '6 Months', days: 180 },
    { id: '1y', label: '1 Year', days: 365 },
    { id: 'all', label: 'All Time', days: 0 },
  ];
  activePeriodPreset = 'today';
  periodStartDate: Date = this.timeService.getTrueDate();
  periodEndDate: Date = this.timeService.getTrueDate();
  showCustomRange = false;
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;

  invoiceTypeTabs: TabItem[] = [
    { id: 'all', label: 'All' },
    // { id: 'DiagnosticReport', label: 'DiagnosticReport' },
    { id: 'ImagingStudy', label: 'Imaging' },
    { id: 'Immunization', label: 'Immunization' },
    { id: 'MedicationAdministration', label: 'MedicationAdministration' },
    { id: 'MedicationDispense', label: 'MedicationDispense' },
    // { id: 'Observation', label: 'Observation' },
    { id: 'Procedure', label: 'Procedure' },
    { id: 'SupplyDelivery', label: 'SupplyDelivery' },
    { id: 'Laboratory', label: 'Laboratory' },
    { id: 'other', label: 'other' },
  ];
  private patientIdFromRoute: string | null = null;
  // add an encounter-group column (like "groupReport"/requisition grouping)
  override displayedColumns = ['encounterGroup', 'date', 'status', 'subject', 'type', 'total', 'view'];

  detailsBuilder: DetailsBuilderObject = {
    resourceName: 'Invoice',
    resourceIcon: 'receipt_long',
    specialHeader: {
      strongSectionKey: 'Invoice',
      iconSectionKeys: ['status'],
      contentSectionKeys: ['Invoice Type', 'Subject', 'Date', 'Total']
    },
    groups: [
      { groupName: 'Invoice', groupIcon: 'receipt_long', groupKeys: ['Invoice', 'status', 'Date', 'Total', 'Issuer'] },
      { groupName: 'Context', groupIcon: 'account_circle', groupKeys: ['Subject', 'Encounter'] },
      { groupName: 'Classification', groupIcon: 'category', groupKeys: ['Invoice Type'] },
      { groupName: 'Charge Items', groupIcon: 'payments', groupKeys: ['Charge Items Count', 'Charge Items'] }
    ]
  };

  // Replaced row-spanning logic with inline group badges for performance

  private invoiceDataSub?: any;

  override ngOnInit(): void {
    this.computePermissions('invoice');
    this.computeDateRange();

    // Subscribe to query parameters to support inline patient filtering and date updates
    this.route.queryParams.subscribe(params => {
      const queryPatientId = params['patientId'];
      this.patientIdFromRoute = this.route.snapshot.paramMap.get('patientId');

      const currentPatientId = this.stateService.currentPatientId$.getValue();
      this.effectivePatientId = queryPatientId ?? this.patientIdFromRoute ?? currentPatientId ?? null;
      const shouldUsePatientScope = !!this.effectivePatientId && (!this.patientIdFromRoute || this.patientIdFromRoute === currentPatientId);

      if (this.effectivePatientId) {
        this.displayedColumns = ['encounterGroup', 'date', 'status', 'type', 'total', 'view'];
      } else {
        this.displayedColumns = ['encounterGroup', 'date', 'status', 'subject', 'type', 'total', 'view'];
      }

      // Stop query if no patient and no date range (All Time selected)
      if (!this.effectivePatientId && this.activePeriodPreset === 'all') {
        // Enforce two tier filtering: Must choose patient or a time interval
        this.immutableData = [];
        this.tableDataSource.data = [];
        this.executeTransforms();
        return;
      }

      const scope: 'patient' | 'orgWide' = shouldUsePatientScope ? 'patient' : 'orgWide';

      const invoicesQueryArgs: any = {
        resourceType: 'Invoice',
        scope,
        count: scope === 'patient' ? 200 : 1000,
        tier1FollowNextLinks: true,
        tier3TriggerWhenBelowExpectedMinOnTier1Failure: false,
        urlSearchObject: {},
        querySearchObject: [] as QuerySearchCriterion[]
      };

      if (this.activePeriodPreset !== 'all') {
        invoicesQueryArgs.urlSearchObject.date = [`ge${this.toFhirDate(this.periodStartDate)}`, `le${this.toFhirDate(this.periodEndDate)}`];
        this.applyFilter('dateRange', (row: any) => {
          const inv = row?.actualResource ?? row;
          const d = new Date(inv?.date ?? inv?.meta?.lastUpdated ?? 0);
          const start = new Date(this.periodStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(this.periodEndDate);
          end.setHours(23, 59, 59, 999);
          return d >= start && d <= end;
        });
      } else {
        this.removeFilter('dateRange');
      }

      if (this.effectivePatientId) {
        invoicesQueryArgs.urlSearchObject = {
          ...invoicesQueryArgs.urlSearchObject,
          subject: `Patient/${this.effectivePatientId}`
        };
        invoicesQueryArgs.querySearchObject = [
          { key: 'subject.reference', value: [`Patient/${this.effectivePatientId}`, `${this.backendEndPoint}/Patient/${this.effectivePatientId}`] }
        ];
        if (shouldUsePatientScope) {
          invoicesQueryArgs.patientId = this.effectivePatientId;
          invoicesQueryArgs.patientScopeSearchParam = 'subject';
        }
      }

      const invoices$ = this.utilityService.threeTierQueryAndSyncToState$<Invoice>(invoicesQueryArgs);

      const transformedInvoices$ = invoices$.pipe(
        map((entries: any[]) => {
          const invoices = (entries ?? []).filter((r) => r.resourceType === 'Invoice') as Invoice[];
          const chargeItems = (entries ?? []).filter((r) => r.resourceType === 'ChargeItem') as ChargeItem[];

          chargeItems.forEach(ci => {
            if (ci.id) this.chargeItemCache.set(ci.id, ci);
          });

          return invoices.filter((inv: Invoice) => {
            const lineItems = ((inv?.lineItem ?? []) as Invoice['lineItem']) || [];
            return lineItems.some((li) => {
              const ref = (li?.chargeItemReference?.reference ?? '').toString().trim();
              return ref.length > 0;
            });
          });
        }),
        switchMap(async (invoices: Invoice[]) => {
          const allMissingIds = new Set<string>();
          invoices.forEach(inv => {
            const lineItems = ((inv as any)?.lineItem ?? []) as any[];
            lineItems.forEach(li => {
              const ref = (li?.chargeItemReference?.reference ?? '').toString().trim();
              const id = this.extractIdFromRef(ref);
              if (id && !this.chargeItemCache.has(id)) {
                allMissingIds.add(id);
              }
            });
          });

          if (allMissingIds.size > 0) {
            try {
              const idsArray = Array.from(allMissingIds);
              const chunkSize = 50;
              for (let i = 0; i < idsArray.length; i += chunkSize) {
                const chunk = idsArray.slice(i, i + chunkSize);
                const bundle = await firstValueFrom(
                  this.http.get<Bundle<ChargeItem>>(`${this.backendEndPoint}/ChargeItem?_id=${chunk.join(',')}`, {
                    context: new HttpContext().set(DisableSortParam, true)
                  }).pipe(catchError(() => of(null)))
                );
                if (bundle && bundle.entry) {
                  bundle.entry.forEach(e => {
                    if (e.resource?.id) this.chargeItemCache.set(e.resource.id, e.resource);
                  });
                }
              }
            } catch (err) {
              console.error('Batch fetch for missing ChargeItems failed', err);
            }
          }

          const mapped = await Promise.all(
            (invoices || []).map(async (invoice) => ({
              actualResource: await this.transformInvoiceRow(invoice)
            }))
          );

          const sorted = mapped
            .map(m => m.actualResource)
            .filter(Boolean)
            .sort((a: any, b: any) => {
              const da = new Date((a as any)?.date ?? (a as any)?.meta?.lastUpdated ?? 0).getTime();
              const db = new Date((b as any)?.date ?? (b as any)?.meta?.lastUpdated ?? 0).getTime();
              return db - da;
            });

          return sorted.map((inv: any) => ({ actualResource: inv }));
        })
      );

      if (this.invoiceDataSub) {
        this.invoiceDataSub.unsubscribe();
        this.subs.remove(this.invoiceDataSub);
      }

      this.invoiceDataSub = transformedInvoices$.subscribe({
        next: (data) => {
          data = data.map((entry: any) => entry.actualResource || entry);
          this.immutableData = data;
          this.tableDataSource.data = data;
          this.applyInvoiceTypeTab(this.invoiceTypeTab);
          this.executeTransforms();
        },
        error: (err) => {
          this.errorService.openandCloseError('Failed to load invoices data.' + err);
        }
      });
      this.subs.add(this.invoiceDataSub);
    });

    // filters (status + type)
    this.organizingSearchFilters(new Map<string, any[]>([
      ['status', ['issued', 'balanced', 'cancelled', 'draft', 'entered-in-error']],
      ['Invoice Type', ['DiagnosticReport', 'ImagingStudy', 'Immunization', 'MedicationAdministration', 'MedicationDispense', 'Observation', 'Procedure', 'SupplyDelivery', 'Laboratory', 'other']]
    ]));

    // initial tab filter (all)
    this.applyInvoiceTypeTab('all');

    // Example: get all query params (refresh-safe, decode-safe)
    this.incomingQueryParams = this.utilityService.getAllQueryParamsNormalized(this.route);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    // if (this.patientIdFromRoute && this.stateService.currentPatientIdFromResolver.getValue() === this.patientIdFromRoute) {
    //   this.stateService.currentPatientIdFromResolver.next(null);
    // }
  }
  incomingQueryParams?: any
  route = inject(ActivatedRoute)

  override executeTransforms(): void {
    super.executeTransforms();

    const invoices = [...this.tableDataSource.data];

    // Compute encounter group counts
    const encounterGroupCounts: { [key: string]: number } = {};
    invoices.forEach((inv) => {
      const groupKey = inv['__encounterGroupKey'];
      if (groupKey && groupKey !== 'N/A') {
        encounterGroupCounts[groupKey] = (encounterGroupCounts[groupKey] || 0) + 1;
      }
    });

    invoices.forEach((inv) => {
      const groupKey = inv['__encounterGroupKey'];
      if (groupKey && groupKey !== 'N/A') {
        inv['_groupCount'] = encounterGroupCounts[groupKey];
        inv['_encounterGroupKeyString'] = String(groupKey);
      }
    });

    this.tableDataSource.data = invoices;
  }

  private async transformInvoiceRow(inv: Invoice): Promise<Invoice & Record<string, any>> {
    const relatedChargeItems = await this.fetchRelatedChargeItemsForInvoice(inv);
    const invoiceType: InvoiceType = relatedChargeItems.length ? this.deriveInvoiceTypeFromChargeItems(relatedChargeItems[0]) : 'other';
// $#$    // /* alert(invoiceType) */s

    // NEW: encounter grouping key (prefer ChargeItem.context.reference, because you said ChargeItem has encounter property)
    console.log('Deriving encounter grouping for invoice:', relatedChargeItems, inv);
    const encounterRefFromCI =
      relatedChargeItems.map(ci => (ci as any)?.context?.reference).find((r: any) => typeof r === 'string' && r.includes('Encounter/')) ||
      null;

    const encounterRef =
      encounterRefFromCI ||
      (inv as any)?.encounter?.reference ||
      (inv as any)?.context?.reference ||
      null;

    const dateRaw = (inv as any)?.date ?? inv?.meta?.lastUpdated ?? '';
    const d = dateRaw ? new Date(dateRaw) : null;

    const total = (inv as any)?.totalGross || (inv as any)?.totalNet || null;
    const totalDisplay = total?.value != null
      ? new Intl.NumberFormat('en-NG', { style: 'currency', currency: total?.currency || 'NGN' }).format(Number(total.value))
      : 'N/A';

    return {
      ...(inv as any),

      // grouping key used by template neighbor checks
      __encounterGroupKey: encounterRef || 'N/A',

      // user-friendly fields (keys with spaces)
      'Invoice': inv.id ? `Invoice/${inv.id}` : 'Invoice',
      'Date': d ? d.toLocaleString() : 'N/A',
      'Day': d ? d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' }) : 'N/A',
      'Time': d ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '',
      'Subject': inv.subject?.reference || inv.subject?.display || 'N/A',
      'Encounter': encounterRef || 'N/A',
      'Invoice Type': invoiceType,
      'Total': totalDisplay,

      relatedChargeItems,
      'Charge Items Count': relatedChargeItems.length
    };
  }

  private async fetchRelatedChargeItemsForInvoice(inv: Invoice): Promise<ChargeItem[]> {
    const lineItems = ((inv as any)?.lineItem ?? []) as any[];
    const ids = lineItems
      .map(li => (li?.chargeItemReference?.reference ?? '').toString())
      .map(ref => this.extractIdFromRef(ref))
      .filter((id): id is string => !!id);

    if (!ids.length) return [];

    const result: ChargeItem[] = [];
    const missingIds: string[] = [];

    // 1. Check local cache
    ids.forEach(id => {
      const cached = this.chargeItemCache.get(id);
      if (cached) {
        result.push(cached);
      } else {
        missingIds.push(id);
      }
    });

    if (missingIds.length > 0) {
      // Fallback individual fetch if missed during batch pre-fetch
      console.warn('ChargeItems missed during batch fetch:', missingIds);
      try {
        const bundle = await firstValueFrom(
          this.http.get<Bundle<ChargeItem>>(`${this.backendEndPoint}/ChargeItem?_id=${missingIds.join(',')}`, {
            context: new HttpContext().set(DisableSortParam, true)
          }).pipe(catchError(() => of(null)))
        );
        if (bundle && bundle.entry) {
          bundle.entry.forEach(e => {
            if (e.resource) {
              result.push(e.resource);
              if (e.resource.id) this.chargeItemCache.set(e.resource.id, e.resource);
            }
          });
        }
      } catch (err) {
        console.error('Fallback fetch for missing ChargeItems failed', err);
      }
    }

    return result;
  }

  private extractIdFromRef(ref?: string): string | null {
    const r = (ref ?? '').trim();
    if (!r) return null;
    const parts = r.split('/');
    return parts.length ? (parts[parts.length - 1] || null) : null;
  }

  private deriveInvoiceTypeFromChargeItems_old(ci: ChargeItem): InvoiceType {

    let pr = ((ci as any)?.service?.[0]?.reference?.split('/')[0] ??
      (ci as any)?.productReference?.reference?.split('/')[0] ?? ci.code.coding?.[0]?.code?.toString().toLowerCase() ?? ''
    ).toString();
    if (!pr) {
      //then check code
    }
    pr = pr.toString().toLowerCase();

    // Medication Dispense pattern: productReference points to Medication/...
    if (ci.service && ci.service.length > 0) {
      return ci.service[0].reference?.split('/')[0] as any;
    }

    // Laboratory: usually ChargeItem is for an Observation (lab result) or a lab ServiceRequest (if you front-load billing)
    if (pr.startsWith('lab')) return 'Laboratory';
    if (pr.includes('medication')) return 'MedicationDispense';


    return 'other';
  }

  private deriveInvoiceTypeFromChargeItems(ci: ChargeItem): InvoiceType {
    const ref = ((ci as any)?.service?.[0]?.reference ?? (ci as any)?.productReference?.reference ?? '').toString().trim();
    const refType = (ref.split('/')[ref.split('/').length - 2] ?? '').toString().trim();

    const allowed: Record<string, InvoiceType> = {
      DiagnosticReport: 'DiagnosticReport',
      ImagingStudy: 'ImagingStudy',
      Immunization: 'Immunization',
      MedicationAdministration: 'MedicationAdministration',
      MedicationDispense: 'MedicationDispense',
      Observation: 'Observation',
      Procedure: 'Procedure',
      SupplyDelivery: 'SupplyDelivery',
      Laboratory: 'Laboratory'
    };

    // if (refType && allowed[refType]) return allowed[refType];

    // Project convention: ServiceRequest-based billing is treated as "Laboratory" on the invoice list
    if (refType.toLowerCase() === 'servicerequest') return 'Laboratory';



    const codeRaw = (ci?.code?.coding?.[0]?.code ?? '').toString().trim();
    const prefix = (codeRaw.split('-')[0] ?? '').toString().trim().toLowerCase();
    if (codeRaw.toLowerCase().includes('med-disp')) {
      return 'MedicationDispense';
    }
    const inferred: Record<string, InvoiceType> = {
      // diagnosticreport: 'DiagnosticReport',
      'imaging': 'ImagingStudy',
      immunization: 'Immunization',
      medicationadministration: 'MedicationAdministration',
      medicationdispense: 'MedicationDispense',
      observation: 'Observation',
      procedure: 'Procedure',
      supplydelivery: 'SupplyDelivery',
      laboratory: 'Laboratory'
    };

    if (prefix && inferred[prefix]) return inferred[prefix];
    if (ci.code && String(ci.code?.coding?.[0]?.code || "").split("_")?.[0]) {
      return inferred[String(ci.code?.coding?.[0]?.code || "").split("_")?.[0].toLowerCase()]
    }

    return 'other';
  }

  // --- Date Period Filtering ---

  private computeDateRange() {
    this.periodEndDate = this.timeService.getTrueDate();

    if (this.activePeriodPreset === 'custom' && this.customStartDate && this.customEndDate) {
      this.periodStartDate = this.customStartDate;
      this.periodEndDate = this.customEndDate;
    } else if (this.activePeriodPreset === 'today') {
      this.periodStartDate = this.timeService.getTrueDate();
      this.periodEndDate = this.timeService.getTrueDate();
    } else {
      const preset = this.periodPresets.find(p => p.id === this.activePeriodPreset);
      if (preset && preset.days > 0) {
        this.periodStartDate = this.timeService.getTrueDate();
        this.periodStartDate.setDate(this.periodStartDate.getDate() - preset.days);
      } else {
        // 'all' — no date filtering
        this.periodStartDate = new Date(2000, 0, 1);
      }
    }
  }

  private toFhirDate(date: Date): string {
    return this.timeService.toFhirDate(date);
  }

  getFormattedPeriodRange(): string {
    if (this.activePeriodPreset === 'all') return 'All available records';
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const start = this.periodStartDate.toLocaleDateString('en-US', options);
    const end = this.periodEndDate.toLocaleDateString('en-US', options);
    return `${start} — ${end}`;
  }

  onPeriodChanged(presetId: string) {
    if (presetId === this.activePeriodPreset) return;
    this.activePeriodPreset = presetId;
    this.showCustomRange = false;
    this.computeDateRange();
    this.reloadInvoices();
  }

  onCustomDateApplied() {
    if (!this.customStartDate || !this.customEndDate) return;
    this.activePeriodPreset = 'custom';
    this.computeDateRange();
    this.showCustomRange = false;
    this.reloadInvoices();
  }

  toggleCustomRange() {
    this.showCustomRange = !this.showCustomRange;
  }

  private reloadInvoices() {
    // Re-trigger query parameter subscriber to refresh invoices
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { _r: new Date().getTime() },
      queryParamsHandling: 'merge'
    });
  }

  applyInvoiceTypeTab(tabId: 'all' | InvoiceType | string): void {
    const tab = (tabId ?? '').toString().trim();
    this.invoiceTypeTab = (tab || 'all') as any;

    if (tab === 'all' || !tab) {
      this.removeFilter('invoiceType');
      return;
    }

    this.applyFilter('invoiceType', (row: any) => {
      const inv = row?.actualResource ?? row;
      const t = (inv?.['Invoice Type'] ?? '').toString().trim();
      console.log('Filtering invoice type tab:', tab, t);
      return t === tab;
    });
  }

  showRow(row: any): void {
    this.dialog.open(DetailzViewzComponent, {
      maxHeight: '93vh',
      maxWidth: '90vw',
      data: {
        resourceData: row,
        detailsBuilderObject: this.detailsBuilder,
        excludeKeys: ['text', 'contained', 'meta']
      }
    });
  }
}